import axios from 'axios';
import { API } from '../contexts/AuthContext';

/** Mirrors backend/src/services/objectDetection.ts DetectedObject. */
export interface DetectedObject {
  id: string;
  label: string;
  box: { ymin: number; xmin: number; ymax: number; xmax: number }; // 0–1000 normalized
}

/**
 * Keywords that have a product-category rule server-side.
 *
 * This mirrors the union of the rules in `backend/src/lib/sceneShop.ts`, and
 * exists only to pick which labels are worth asking the panel for — the backend
 * stays authoritative for what actually appears in it. If the two drift, the
 * failure is cosmetic in both directions: a too-permissive list sends a label
 * the server drops, a too-strict one costs a tab. Content is never wrong, and
 * the panel itself is always reachable (a label-less request browses the whole
 * catalog). Keep them in sync when adding a category.
 */
const SHOPPABLE_LABEL_RE =
  /\b(floor lamp|table lamp|desk lamp|pendant|chandelier|sconce|ceiling light|light fixture|lamp|rug|carpet|runner|curtains?|drapes?|blinds?|cushion|pillow|throw|blanket|quilt|duvet|wall art|artwork|painting|poster|picture frame|photo frame|mirror|vase|sculpture|figurine|candle|clock|plants?|flowers?|greenery|tv unit|tv stand|media console|entertainment cent(er|re)|bookshelf|bookcase|shelves|shelving|shelf|cabinet|wardrobe|dresser|chest of drawers|sideboard|console|basket|storage|sofa|couch|sectional|loveseat|settee|divan|armchair|recliner|chair|stool|bench|ottoman|pouffe|pouf|coffee table|side table|end table|dining table|nightstand|bedside table|desk|table|bed|headboard|mattress)\b/i;

/** Strip the counter object detection appends to repeated labels ("sofa 2"). */
export function normalizeLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s+\d+$/, '');
}

/**
 * Detected objects -> deduped labels worth offering products for, in detection
 * order (most prominent first). Empty when nothing in the room is shoppable.
 */
export function shoppableLabels(objects: DetectedObject[] | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of objects ?? []) {
    const label = normalizeLabel(o?.label ?? '');
    if (!label || seen.has(label) || !SHOPPABLE_LABEL_RE.test(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

export async function detectObjects(
  projectId: string,
  target: { type: 'original' } | { type: 'generation'; generationId: string },
  force = false,
): Promise<DetectedObject[]> {
  const body =
    target.type === 'generation'
      ? { target: 'generation', generationId: target.generationId, force }
      : { target: 'original', force };
  const res = await axios.post<{ objects: DetectedObject[] }>(
    `${API}/projects/${projectId}/detect`,
    body,
  );
  return res.data.objects;
}
